// src/components/ToggleButton.js

import React, { useEffect, useRef } from 'react';
import ProactiveBubble from './ProactiveBubble';
import './ChatbotWidget.css';

// Premium notification sound inspired by iOS - clean, crisp, and pleasant
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();

    // Create a clean, bell-like tone with natural harmonics
    const playTone = (frequency, startTime, duration, volume = 0.12) => {
      // Main tone
      const osc1 = audioContext.createOscillator();
      const gain1 = audioContext.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(frequency, startTime);

      // Subtle harmonic for richness (like iOS sounds)
      const osc2 = audioContext.createOscillator();
      const gain2 = audioContext.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(frequency * 2, startTime); // Octave harmonic

      // Connect and set volume envelopes
      osc1.connect(gain1);
      osc2.connect(gain2);
      gain1.connect(audioContext.destination);
      gain2.connect(audioContext.destination);

      // Clean, crisp envelope - quick attack, natural decay
      gain1.gain.setValueAtTime(0, startTime);
      gain1.gain.linearRampToValueAtTime(volume, startTime + 0.01);
      gain1.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

      gain2.gain.setValueAtTime(0, startTime);
      gain2.gain.linearRampToValueAtTime(volume * 0.15, startTime + 0.01);
      gain2.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

      osc1.start(startTime);
      osc1.stop(startTime + duration);
      osc2.start(startTime);
      osc2.stop(startTime + duration);
    };

    const now = audioContext.currentTime;
    // Clean two-tone sequence: G5 (784Hz) -> B5 (988Hz) - pleasant major third, crisp and refined
    playTone(784, now, 0.18, 0.11);
    playTone(988, now + 0.08, 0.2, 0.1);

  } catch (e) {
    // Silently fail if audio is not available
  }
};

function ToggleButton({
  isChatOpen,
  toggleChat,
  isPlatform,
  chatbotPosition,
  toggleButtonSize = 'medium',
  toggleButtonIcon = '',
  customToggleButtonIcon = '',
  toggleButtonIconSize = '60',
  toggleButtonBackgroundColor = '#1a1a1a',
  toggleButtonIconOffsetX = 0,
  toggleButtonIconOffsetY = 0,
  toggleButtonOutlineEnabled = false,
  toggleButtonOutlineColor = '#ffffff',
  headerColor = '#4a90e2',
  // Proactive bubble props
  proactiveBubbleMessage = '',
  showProactiveBubble = false,
  onProactiveBubbleDismiss,
  onProactiveBubbleAccept,
  // User toggle props
  userSuggestionsEnabled = true,
  onSuggestionsToggle,
  // Notification props
  unreadCount = 0,
  soundNotificationEnabled = true
}) {
  // Track previous unread count to detect new messages
  const previousUnreadRef = useRef(0);

  // Play sound when unread count increases
  useEffect(() => {
    if (unreadCount > previousUnreadRef.current && unreadCount > 0) {
      if (soundNotificationEnabled) {
        playNotificationSound();
      }
    }
    previousUnreadRef.current = unreadCount;
  }, [unreadCount, soundNotificationEnabled]);

  // Determine API base URL for asset loading
  const getAssetBaseUrl = () => {
    if (isPlatform) return '';

    // For embedded widgets, try to detect origin from script
    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
      if (scripts[i].src && scripts[i].src.includes('smushchat-widget.js')) {
        try {
          const url = new URL(scripts[i].src);
          // If localhost, return empty string as we might be proxying or same-origin in dev
          // But for production assets, we need the origin
          return url.origin;
        } catch (e) {
          return 'https://smushlabs.ai';
        }
      }
    }
    return 'https://smushlabs.ai';
  };

  // Determine which icon to use
  const rawIconUrl = customToggleButtonIcon || toggleButtonIcon || '/icons/default-icon.svg';

  // Resolve absolute URL for icons if they are relative paths and we are not on the platform
  const iconUrl = (!isPlatform && rawIconUrl.startsWith('/'))
    ? `${getAssetBaseUrl()}${rawIconUrl}`
    : rawIconUrl;

  return (
    !isChatOpen && (
      <>
        <ProactiveBubble
          message={proactiveBubbleMessage}
          isVisible={showProactiveBubble}
          position={chatbotPosition || 'right'}
          onDismiss={onProactiveBubbleDismiss}
          onAccept={onProactiveBubbleAccept}
          suggestionsEnabled={userSuggestionsEnabled}
          onToggleOff={onSuggestionsToggle}
          isPlatform={isPlatform}
        />
        <div
          id="chatbot-toggle-button"
          className={`toggle-button ${isPlatform ? 'platform' : 'iframe'} ${chatbotPosition} ${toggleButtonSize}`}
          onClick={toggleChat}
          aria-label="Open Chatbot"
          style={{
            backgroundColor: toggleButtonBackgroundColor,
            backgroundImage: iconUrl ? `url("${iconUrl}")` : 'none',
            backgroundSize: `${toggleButtonIconSize}%`,
            backgroundPosition: `calc(50% + ${toggleButtonIconOffsetX}px) calc(50% + ${toggleButtonIconOffsetY}px)`,
            backgroundRepeat: 'no-repeat',
            border: toggleButtonOutlineEnabled ? `3px solid ${toggleButtonOutlineColor}` : 'none',
            boxShadow: toggleButtonOutlineEnabled ? `0 0 0 2px rgba(0,0,0,0.1), 0 4px 12px rgba(0,0,0,0.15)` : 'none'
          }}
        >
          {/* Notification Badge - subtle dot indicator */}
          {unreadCount > 0 && (
            <div
              className="toggle-button-badge"
              aria-label={`${unreadCount} unread messages`}
              style={{
                background: headerColor,
                boxShadow: `0 2px 8px rgba(255, 107, 107, 0.5), 0 0 12px rgba(255, 107, 107, 0.3)`
              }}
            />
          )}
        </div>
      </>
    )
  );
}

export default ToggleButton;
